import { useState, useEffect } from "react";
import { Grid, Typography, Box, Button } from "@mui/material";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import styles from "./style.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { api, resolveUrl } from "../../../config/apis";
import StudentApi from "../../../config/apis/student";
import PreAssessmentForm from "./edit";
import Moment from "react-moment";
import { GetUserId, GetUserRole } from "../../../helper/helperFunction";

export default function PreAssessmentFormView() {
  const nevigate = useNavigate();
  const studentId = GetUserId();

  const isStudent = GetUserRole() === "Student";
  console.log("Student ID:", isStudent);

  const params = useParams();
  const stu_Id = params.id;

  const [formData, setFormData] = useState();
  const [formSuccess, setFormSuccess] = useState(false);
  
  const onUpdate = async () => {
    nevigate(
      isStudent
        ? "/admin/pre-assessment-form/"
        : `/admin/user/pre-assessment-form/${stu_Id}`,
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(
          resolveUrl(
            StudentApi.GET_PRE_ASSESSMENT_FORM_DATA,
            isStudent ? studentId : stu_Id ?? ""
          )
        );
        setFormData(res.data.data);
      } catch (error) {
        console.error("Error fetching general data:", error);
      }
    };

    fetchData();
  }, [formSuccess]);

  const renderFormData = () => {
    if (!formData) {
      return "There are no records to display";
    }

    // Exclude fields that shouldn't be displayed or are handled specially
    const excludedFields = [
      "state_id",
      "city_id",
      "country_id",
      "registration_number",
      "highest_qualifications",
      "join_reason_other",
      "coding_language", // Handle this with skills
    ];

    const filteredFormData = Object.fromEntries(
      Object.entries(formData).filter(([key]) => !excludedFields.includes(key))
    );

    return Object.entries(filteredFormData).map(([key, value]) => {
      // Skip empty or null values
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return null;
      }

      // Skip zero values for specific fields
      if (
        (key === "number_of_kids" ||
          key === "kids_education_numbers" ||
          key === "number_of_siblings" ||
          key === "siblings_education_numbers" ||
          key === "number_of_family_members" ||
          key === "number_of_employed_family_members" ||
          key === "laptop_count" ||
          key === "desktop_count" ||
          key === "smartphone_count" ||
          key === "internet_count") &&
        value === 0
      ) {
        return null;
      }

      // Format the key for display
      const formattedKey = key.replace(/_/g, " ");

      // Custom display names for specific fields
      const displayNameMap = {
        laptopdesktop: "Laptop Desktop",
        smartphone: "Smart Phone",
        qualification: "Highest Qualification",
        join_reasons: "Why do you want to join this training?",
        cnic: "CNIC",
        laptop_count: "Number of Laptops",
        desktop_count: "Number of Desktops",
        smartphone_count: "Number of Smartphones",
        internet_count: "Number of Internet Connections",
        spoken_english_rating: "Spoken English Rating",
        written_english_rating: "Written English Rating",
        expect_learn: "What do you expect to learn?",
        skills: "Do You Have Any Of The Following Skills?",
      };

      const displayName = displayNameMap[key] || formattedKey;

      // Special handling for join_reasons to show "Other" reason
      if (key === "join_reasons") {
        const reasonsArray = Array.isArray(value) 
          ? value 
          : value.split(',').map(item => item.trim());

        return (
          <Grid key={key} item xl={4} lg={6} xs={12} className={styles.grid_col}>
            <Box className={styles.box}>
              <Typography
                variant="body1"
                className={`${styles.label_key} ${key}`}
              >
                {displayName}
              </Typography>
              <Box>
                {reasonsArray.map((item, index) => (
                  <Typography key={index} variant="body1" fontWeight={700}>
                    • {item}
                  </Typography>
                ))}
                {formData.join_reason_other &&
                  reasonsArray.some(
                    (item) => item.toLowerCase().trim() === "other"
                  ) && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        ml: 2,
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      Other reason: {formData.join_reason_other}
                    </Typography>
                  )}
              </Box>
            </Box>
          </Grid>
        );
      }

      // Special handling for skills - supports both old (array/string) and new (array of objects) formats
      if (key === "skills") {
        // Check if skills is an array of objects (new format with skill and category)
        if (Array.isArray(value) && value.length > 0 && value[0].hasOwnProperty('skill') && value[0].hasOwnProperty('category')) {
          // New format: Array of objects with skill and category properties
          // Group skills by category
          const skillsByCategory = value.reduce((acc, item) => {
            const category = item.category;
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push(item.skill);
            return acc;
          }, {});

          return (
            <Grid key={key} item xs={12} className={styles.grid_col}>
              <Box className={styles.box}>
                <Typography
                  variant="body1"
                  className={`${styles.label_key} ${key}`}
                >
                  {displayName}
                </Typography>
                <Box>
                  {Object.entries(skillsByCategory).map(([category, skills], index) => {
                    return (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography 
                          variant="subtitle1" 
                          fontWeight={600}
                          sx={{ mb: 0.5, mt: 1 }}
                        >
                          {category}
                        </Typography>
                        {skills.map((skill, skillIndex) => (
                          <Typography 
                            key={skillIndex} 
                            variant="body1" 
                            fontWeight={700}
                            sx={{ ml: 2 }}
                          >
                            • {skill}
                          </Typography>
                        ))}
                      </Box>
                    );
                  })}
                  {/* Display coding language if "Coding / Programming" exists in any category */}
                  {formData.coding_language && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        ml: 2,
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      Coding Language: {formData.coding_language}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        } else {
          // Old format: Simple array or comma-separated string
          let skillsArray = [];

          if (Array.isArray(value)) {
            // Clean up the array - remove escape characters and brackets
            skillsArray = value.map(item =>
              typeof item === 'string' ? item.replace(/[\[\]"\\]/g, '').trim() : item
            ).filter(item => item !== '');
          } else {
            skillsArray = value.split(',').map(item => item.trim());
          }

          return (
            <Grid key={key} item xs={12} className={styles.grid_col}>
              <Box className={styles.box}>
                <Typography
                  variant="body1"
                  className={`${styles.label_key} ${key}`}
                >
                  {displayName}
                </Typography>
                <Box>
                  {skillsArray.map((item, index) => (
                    <Typography key={index} variant="body1" fontWeight={700}>
                      • {item}
                    </Typography>
                  ))}
                  {formData.coding_language &&
                    skillsArray.some((item) =>
                      item.toLowerCase().includes("coding")
                    ) && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          ml: 2,
                          fontStyle: "italic",
                          color: "text.secondary",
                        }}
                      >
                        Coding Language: {formData.coding_language}
                      </Typography>
                    )}
                </Box>
              </Box>
            </Grid>
          );
        }
      }

      // Default rendering for all other fields
      return (
        <Grid key={key} item xl={4} lg={6} xs={12} className={styles.grid_col}>
          <Box className={styles.box}>
            <Typography
              variant="body1"
              className={`${styles.label_key} ${key}`}
            >
              {displayName}
            </Typography>
            {Array.isArray(value) ? (
              <Box>
                {value.map((item, index) => (
                  <Typography key={index} variant="body1" fontWeight={700}>
                    • {typeof item === 'string' ? item.trim() : item}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body1" fontWeight={700}>
                {key === "registration_date" || key === "date_of_birth" ? (
                  <Moment format="DD/MM/YYYY">{value}</Moment>
                ) : (
                  value
                )}
              </Typography>
            )}
          </Box>
        </Grid>
      );
    });
  };

  return (
    <>
      {studentId && !formData ? (
        <PreAssessmentForm setFormSuccess={setFormSuccess} />
      ) : (
        <Box
          className={`container global-user ${styles.preAssessmentFormContainer} ${styles.PreAssessmentFormView}`}
        >
          <Typography variant="h1" className="main-heading">
            Pre Assessment
          </Typography>
          <Grid container className={`page-container ${styles.addStudent}`}>
            <Grid container spacing={2}>
              {renderFormData()}
            </Grid>
            <Grid container justifyContent={"flex-end"} marginTop={5}>
              {studentId && (
                <Button
                  className={styles.btn}
                  type="submit"
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  onClick={() => onUpdate()}
                >
                  Update
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
